import { Fragment, useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { HostEnvironment, hostUrlHelper, locHrefUtil, SALES_CHECKIN_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';

import { DDJ_ENV_BY_HOST, DDJ_PRELOADER_ID, DDJ_SCRIPT_ID, DDJ_SOURCE_URLS, DEFAULT_PARTNER_ID } from '../constants';
import { getDealRefId, getDealXgId, getDefaultPartnerId, getPartnerCode, getPartnerDealerId, getSource } from '../store/config';
import type { DDJPreloaderProps, DDJPreloaderStatus } from '../types';
import '../custom.d';

import { DDJPreloaderWrapper } from './digitalDealJacket.style';

const DDJPreloader = ({ onStatusChange }: DDJPreloaderProps) => {
    const [status, setStatus] = useState<DDJPreloaderStatus>('idle');

    const dealXgId = useSelector(getDealXgId);
    const dealRefId = useSelector(getDealRefId);
    const partnerCode = useSelector(getPartnerCode);
    const partnerDealerId = useSelector(getPartnerDealerId);

    const externalId = dealXgId;
    const environment = hostUrlHelper.getHostUrlEnvironment(SALES_CHECKIN_APP_PREFIX, locHrefUtil.getLocHref());
    const redirectBaseUrl = `https://${window.location.hostname}`;

    const ddjEnv = DDJ_ENV_BY_HOST[environment];

    const updateStatus = useCallback(
        (newStatus: DDJPreloaderStatus) => {
            setStatus(newStatus);
            onStatusChange?.(newStatus);
        },
        [onStatusChange]
    );

    useEffect(() => {
        // The DDJ PBC assets are served from a CDN whose CORS allow-list does not
        // include the localhost origin, so the micro-frontend cannot load during
        // local development. Skip loading the loader script and surface the error
        // state instead of leaving the preloader stuck on "loading".
        if (environment === HostEnvironment.Local) {
            updateStatus('error');
            return;
        }

        if (customElements.get('ddj-app')) {
            updateStatus('ready');
            return;
        }

        if (document.getElementById(DDJ_SCRIPT_ID)) {
            updateStatus('loading');
            return;
        }

        updateStatus('loading');

        const script = document.createElement('script');
        script.src = DDJ_SOURCE_URLS[ddjEnv];
        script.type = 'text/javascript';
        script.id = DDJ_SCRIPT_ID;

        script.onload = () => {
            updateStatus('ready');
        };

        script.onerror = () => {
            updateStatus('error');
        };

        document.body.appendChild(script);

        return () => {
            const ddjScript = document.getElementById(DDJ_SCRIPT_ID) as HTMLScriptElement | null;
            if (ddjScript) {
                ddjScript.onload = null;
                ddjScript.onerror = null;
                ddjScript.remove();
            }
        };
    }, [updateStatus, environment, ddjEnv]);

    return (
        <DDJPreloaderWrapper id={DDJ_PRELOADER_ID} data-testid="ddj-preloader" data-status={status}>
            {status !== 'error' && Boolean(dealRefId) && (
                // The PBC receives only the dealXgId (external-id) and resolves the
                // deal's dealRefId internally. We defer creating it until the deal
                // HAS a dealRefId (otherwise that internal call fails), and the
                // Fragment key re-creates it if the dealRefId later changes.
                <Fragment key={dealRefId}>
                    <ddj-app
                        data-testid="ddj-preloaded-pbc"
                        external-id={externalId}
                        source={getSource()}
                        env={ddjEnv}
                        redirect-url={redirectBaseUrl}
                        partner-id={partnerCode || getDefaultPartnerId() || DEFAULT_PARTNER_ID}
                        partner-dealer-id={partnerDealerId}
                    ></ddj-app>
                </Fragment>
            )}
        </DDJPreloaderWrapper>
    );
};

export default DDJPreloader;
